import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Descriptions,
  Tag,
  Table,
  Spin,
  Empty,
  Button,
  Modal,
  message,
  Divider,
  Tabs,
  Space,
  Alert,
  Input,
  Typography,
} from "antd";

const { Title, Text } = Typography;
import {
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useCompany } from "../hooks/useCompany";
import {
  LeanCustomsService,
  LeanCustomsOrganization,
  LeanCustomsContract,
  LeanCustomsContractSigner,
  LeanCustomsContractSupportDocument,
  LeanCustomsDeclaration,
  LeanCustomsDeclarationItem,
  LeanCustomsDeclarationDocument,
} from "../services/leancustomsService";
import {
  DECLARATION_STATUS_ORDER,
  DECLARATION_STATUS_COLORS,
  DECLARATION_STATUS_LABELS,
} from "../types/leancustoms";
import { BookingReferenceText } from "../components/BookingReferenceLink";
import { CreateLeanCustomsContract } from "../components/CreateLeanCustomsContract";
import { LeanCustomsIntegrationDetailsContent } from "./Customs/LeanCustomsIntegrationDetailsContent";
import { sortDeclarationDocumentsUtbFirst } from "../utils/leanCustomsDeclarationDocumentsSort";
import { filterDeclarationsForCompanyOrg } from "../utils/leanCustomsDeclarationAccess";

export const Customs: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    companyDetails,
    companyDetailsLoading: companyLoading,
    selectedCompany,
  } = useCompany();
  const company =
    companyDetails && selectedCompany
      ? { ...companyDetails, id: selectedCompany }
      : null;
  const [organization, setOrganization] =
    useState<LeanCustomsOrganization | null>(null);
  const [contracts, setContracts] = useState<LeanCustomsContract[]>([]);
  const [contractSigners, setContractSigners] = useState<
    Record<string, LeanCustomsContractSigner[]>
  >({});
  const [contractSupportDocuments, setContractSupportDocuments] = useState<
    Record<string, LeanCustomsContractSupportDocument[]>
  >({});
  const [loadingSupportDocuments, setLoadingSupportDocuments] = useState<
    Record<string, boolean>
  >({});
  const [declarations, setDeclarations] = useState<LeanCustomsDeclaration[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingSigners, setLoadingSigners] = useState<Record<string, boolean>>(
    {}
  );
  const [loadingDeclarations, setLoadingDeclarations] = useState(false);
  const [selectedDeclaration, setSelectedDeclaration] =
    useState<LeanCustomsDeclaration | null>(null);
  const [declarationModalVisible, setDeclarationModalVisible] = useState(false);
  const [declarationItems, setDeclarationItems] = useState<
    LeanCustomsDeclarationItem[]
  >([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [declarationDocuments, setDeclarationDocuments] = useState<
    LeanCustomsDeclarationDocument[]
  >([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [declarationDocumentsMap, setDeclarationDocumentsMap] = useState<
    Record<string, LeanCustomsDeclarationDocument[]>
  >({});
  const [loadingDocumentsMap, setLoadingDocumentsMap] = useState<
    Record<string, boolean>
  >({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loadingSignupUrl, setLoadingSignupUrl] = useState(false);
  const [contractWithOrganization, setContractWithOrganization] = useState<LeanCustomsContract | null>(null);
  const [checkingContract, setCheckingContract] = useState(false);
  const [linkingOrganization, setLinkingOrganization] = useState(false);
  const [timeUntilNextPoll, setTimeUntilNextPoll] = useState<number>(0);
  const [integrationDetailsModalVisible, setIntegrationDetailsModalVisible] =
    useState(false);

  // Poll contract for organization when contract exists but organization doesn't
  useEffect(() => {
    if (
      company?.leancustoms_contract_id &&
      !company?.leancustoms_organization_id &&
      company.id
    ) {
      const POLL_INTERVAL = 5000; // Poll every 5 seconds
      let lastPollTime = Date.now();
      
      const checkContract = async () => {
        setCheckingContract(true);
        try {
          const result = await LeanCustomsService.getContract(
            company.leancustoms_contract_id!,
            company.id!
          );

          if (result.success && result.data) {
            // Check if contract has an organization_id
            if (result.data.organization_id) {
              setContractWithOrganization(result.data);
            } else {
              setContractWithOrganization(null);
            }
          }
        } catch (error) {
          console.error('Error checking contract:', error);
        } finally {
          setCheckingContract(false);
          lastPollTime = Date.now();
        }
      };

      // Check immediately
      checkContract();

      // Poll every 5 seconds
      const interval = setInterval(checkContract, POLL_INTERVAL);

      // Update countdown every second
      const countdownInterval = setInterval(() => {
        const elapsed = Date.now() - lastPollTime;
        const remaining = Math.max(0, Math.ceil((POLL_INTERVAL - elapsed) / 1000));
        setTimeUntilNextPoll(remaining);
      }, 1000);

      return () => {
        clearInterval(interval);
        clearInterval(countdownInterval);
      };
    } else {
      setContractWithOrganization(null);
      setTimeUntilNextPoll(0);
    }
  }, [company?.leancustoms_contract_id, company?.leancustoms_organization_id, company?.id]);

  // Load organization and related data when company is available
  useEffect(() => {
    if (company?.leancustoms_organization_id && company.id) {
      loadOrganization();
      loadContracts();
      loadDeclarations();
    }
  }, [company?.leancustoms_organization_id, company?.id]);

  const deepLinkDeclarationRef = useRef<string | null>(null);
  const documentsLoadingRef = useRef<Set<string>>(new Set());

  // Open declaration modal when linked from booking view (?declaration=<uuid>)
  useEffect(() => {
    const id = searchParams.get("declaration");
    if (!id) {
      deepLinkDeclarationRef.current = null;
      return;
    }
    if (!company?.id || loadingDeclarations) return;

    const clearDeclarationParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("declaration");
          return next;
        },
        { replace: true }
      );
    };

    const openDeclarationModal = (record: LeanCustomsDeclaration) => {
      if (deepLinkDeclarationRef.current === id) return;
      deepLinkDeclarationRef.current = id;
      setSelectedDeclaration(record);
      setDeclarationItems([]);
      setDeclarationDocuments([]);
      setDeclarationModalVisible(true);
      clearDeclarationParam();
    };

    const fromList = declarations.find((d) => d.id === id);
    if (fromList) {
      openDeclarationModal(fromList);
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await LeanCustomsService.getDeclaration(id, company.id);
      if (cancelled) return;
      if (res.success && res.data) {
        openDeclarationModal(res.data);
      } else {
        message.warning(t("customs.declarationDeepLinkNotFound"));
        clearDeclarationParam();
        deepLinkDeclarationRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    declarations,
    loadingDeclarations,
    company?.id,
    setSearchParams,
    t,
  ]);

  // Load documents for declarations when they are rendered in the table
  // Use a ref to track which declarations we've already started loading
  
  useEffect(() => {
    if (declarations.length > 0 && company?.id) {
      // Load documents for all declarations that don't have documents loaded yet
      declarations.forEach((declaration) => {
        const declarationId = declaration.id;
        // Check if we should load - use ref to prevent duplicate loads
        if (!documentsLoadingRef.current.has(declarationId)) {
          // Check if already loaded or loading using current state
          const isLoaded = declarationDocumentsMap[declarationId] !== undefined;
          const isLoading = loadingDocumentsMap[declarationId] === true;
          
          if (!isLoaded && !isLoading) {
            // Not loaded and not loading, start loading
            documentsLoadingRef.current.add(declarationId);
            loadDeclarationDocumentsForTable(declarationId);
          } else {
            // Already processed, mark in ref to prevent re-checking
            documentsLoadingRef.current.add(declarationId);
          }
        }
      });
    }
    // Only run when declarations array reference changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarations, company?.id, declarationDocumentsMap, loadingDocumentsMap]);

  const loadOrganization = async () => {
    if (!company?.leancustoms_organization_id || !company.id) return;

    setLoading(true);
    try {
      const result = await LeanCustomsService.getOrganization(
        company.leancustoms_organization_id,
        company.id // Security: pass company_id
      );
      if (result.success && result.data) {
        setOrganization(result.data);
      } else {
        message.error(
          result.error || t("customs.errors.failedToLoadOrganization")
        );
      }
    } catch (error) {
      console.error("Error loading organization:", error);
      message.error(t("customs.errors.failedToLoadOrganization"));
    } finally {
      setLoading(false);
    }
  };

  const loadContracts = async () => {
    if (!company?.leancustoms_organization_id || !company.id) return;

    setLoadingContracts(true);
    try {
      const result = await LeanCustomsService.listOrganizationContracts(
        company.leancustoms_organization_id,
        company.id, // Security: pass company_id
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        setContracts(result.data.results);
      } else {
        message.error(
          result.error || t("customs.errors.failedToLoadContracts")
        );
      }
    } catch (error) {
      console.error("Error loading contracts:", error);
      message.error(t("customs.errors.failedToLoadContracts"));
    } finally {
      setLoadingContracts(false);
    }
  };

  const loadContractSigners = async (contractId: string) => {
    if (!company?.id) return;

    if (contractSigners[contractId]) {
      return; // Already loaded
    }

    setLoadingSigners({ ...loadingSigners, [contractId]: true });
    try {
      const result = await LeanCustomsService.listContractSigners(
        contractId,
        company.id, // Security: pass company_id
        { page: 1, page_size: 100 }
      );
      if (result.success && result.data) {
        setContractSigners({
          ...contractSigners,
          [contractId]: result.data.results,
        });
      }
    } catch (error) {
      console.error("Error loading contract signers:", error);
    } finally {
      setLoadingSigners({ ...loadingSigners, [contractId]: false });
    }
  };

  const loadContractSupportDocuments = async (contractId: string) => {
    if (!company?.id) return;

    if (contractSupportDocuments[contractId]) {
      return; // Already loaded
    }

    setLoadingSupportDocuments({
      ...loadingSupportDocuments,
      [contractId]: true,
    });
    try {
      const result = await LeanCustomsService.listContractSupportDocuments(
        contractId,
        company.id // Security: pass company_id
      );
      if (result.success && result.data) {
        setContractSupportDocuments({
          ...contractSupportDocuments,
          [contractId]: result.data.results,
        });
      }
    } catch (error) {
      console.error("Error loading contract support documents:", error);
    } finally {
      setLoadingSupportDocuments({
        ...loadingSupportDocuments,
        [contractId]: false,
      });
    }
  };

  const handleIntegrationContractPanelsChange = (
    activeKeys: string | string[]
  ) => {
    const keys = Array.isArray(activeKeys) ? activeKeys : [activeKeys];
    keys.forEach((contractId) => {
      if (!contractId) return;
      if (!contractSigners[contractId]) {
        void loadContractSigners(contractId);
      }
      if (!contractSupportDocuments[contractId]) {
        void loadContractSupportDocuments(contractId);
      }
    });
  };

  const loadDeclarations = async (search?: string) => {
    if (!company?.leancustoms_organization_id || !company.id) return;

    setLoadingDeclarations(true);
    try {
      const allDeclarations: LeanCustomsDeclaration[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageSize = 100;

      while (hasMorePages) {
        const params: {
          page: number;
          page_size: number;
          q?: string;
        } = { 
          page: currentPage,
          page_size: pageSize,
        };

        if (search && search.trim()) {
          params.q = search.trim();
        }

        const result = await LeanCustomsService.listDeclarations(
          company.leancustoms_organization_id,
          company.id, // Security: pass company_id
          params
        );

        if (result.success && result.data) {
          allDeclarations.push(...result.data.results);
          
          if (result.data.pagination) {
            // Check if there are more pages
            hasMorePages = currentPage < result.data.pagination.total_pages;
            currentPage++;
          } else {
            hasMorePages = false;
          }
        } else {
          message.error(
            result.error || t("customs.errors.failedToLoadDeclarations")
          );
          hasMorePages = false;
        }
      }

      setDeclarations(
        filterDeclarationsForCompanyOrg(
          allDeclarations,
          company.leancustoms_organization_id
        )
      );
    } catch (error) {
      console.error("Error loading declarations:", error);
      message.error(t("customs.errors.failedToLoadDeclarations"));
    } finally {
      setLoadingDeclarations(false);
    }
  };

  const handleDownloadDocument = async (
    contractId: string,
    documentId: string,
    filename: string
  ) => {
    if (!company?.id) return;

    try {
      const blob = await LeanCustomsService.downloadContractSupportDocument(
        contractId,
        documentId,
        company.id // Security: pass company_id
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message.success(t("customs.documentDownloaded"));
    } catch (error) {
      console.error("Error downloading document:", error);
      message.error(t("customs.errors.failedToDownloadDocument"));
    }
  };


  const loadDeclarationItems = async (declarationId: string) => {
    if (!company?.id) return;

    setLoadingItems(true);
    try {
      const result = await LeanCustomsService.listDeclarationItems(
        declarationId,
        company.id // Security: pass company_id
      );
      if (result.success && result.data) {
        // Filter items to only show items for this specific declaration
        // The API sometimes returns items from multiple declarations
        const filteredItems = result.data.results.filter((item: any) => {
          const itemDeclarationId = item.declaration?.id || item.declaration_id;
          return itemDeclarationId === declarationId;
        });
        setDeclarationItems(filteredItems);
      } else {
        console.error("Error loading items:", result.error);
      }
    } catch (error) {
      console.error("Error loading declaration items:", error);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadDeclarationDocuments = async (declarationId: string) => {
    if (!company?.id) return;

    setLoadingDocuments(true);
    try {
      const result = await LeanCustomsService.listDeclarationDocuments(
        declarationId,
        company.id // Security: pass company_id
      );
      if (result.success && result.data) {
        setDeclarationDocuments(
          sortDeclarationDocumentsUtbFirst(result.data.results)
        );
      } else {
        console.error("Error loading documents:", result.error);
      }
    } catch (error) {
      console.error("Error loading declaration documents:", error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Preload declaration documents when the details modal opens.
  useEffect(() => {
    if (
      !declarationModalVisible ||
      !selectedDeclaration?.id ||
      !company?.id
    ) {
      return;
    }
    const declarationId = selectedDeclaration.id;
    let cancelled = false;
    setLoadingDocuments(true);

    const run = async () => {
      try {
        const docRes = await LeanCustomsService.listDeclarationDocuments(
          declarationId,
          company.id
        );
        if (cancelled) return;
        if (docRes.success && docRes.data) {
          setDeclarationDocuments(
            sortDeclarationDocumentsUtbFirst(docRes.data.results)
          );
        } else {
          console.error("Error loading documents:", docRes.error);
          setDeclarationDocuments([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading modal downloadables:", error);
          setDeclarationDocuments([]);
        }
      } finally {
        if (!cancelled) setLoadingDocuments(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [declarationModalVisible, selectedDeclaration?.id, company?.id]);

  const loadDeclarationDocumentsForTable = async (declarationId: string) => {
    if (!company?.id) return;
    
    // Use functional updates to avoid stale closures
    setLoadingDocumentsMap((prev) => {
      if (prev[declarationId]) {
        return prev; // Already loading, don't start again
      }
      return { ...prev, [declarationId]: true };
    });

    try {
      const result = await LeanCustomsService.listDeclarationDocuments(
        declarationId,
        company.id // Security: pass company_id
      );
      if (result.success && result.data) {
        setDeclarationDocumentsMap((prev) => {
          // Only update if not already set (prevent race conditions)
          if (prev[declarationId] === undefined) {
            return {
              ...prev,
              [declarationId]: sortDeclarationDocumentsUtbFirst(
                result.data.results
              ),
            };
          }
          return prev;
        });
      } else {
        console.error("Error loading documents:", result.error);
        // Set empty array on error
        setDeclarationDocumentsMap((prev) => {
          if (prev[declarationId] === undefined) {
            return {
              ...prev,
              [declarationId]: [],
            };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error loading declaration documents:", error);
      // Set empty array on error
      setDeclarationDocumentsMap((prev) => {
        if (prev[declarationId] === undefined) {
          return {
            ...prev,
            [declarationId]: [],
          };
        }
        return prev;
      });
    } finally {
      setLoadingDocumentsMap((prev) => ({ ...prev, [declarationId]: false }));
    }
  };

  const handleDownloadDeclarationDocument = async (
    declarationId: string,
    documentId: string,
    filename: string
  ) => {
    if (!company?.id) return;

    try {
      const blob = await LeanCustomsService.downloadDeclarationDocument(
        declarationId,
        documentId,
        company.id // Security: pass company_id
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message.success(t("customs.documentDownloaded"));
    } catch (error: any) {
      console.error("Error downloading document:", error);
      const errorMessage = error?.message || t("customs.errors.failedToDownloadDocument");
      message.error(errorMessage);
    }
  };

  const handleDownloadSignerDocument = async (
    contractId: string,
    signerId: string,
    signerName: string
  ) => {
    if (!company?.id) return;

    try {
      const blob = await LeanCustomsService.downloadContractSignerDocument(
        contractId,
        signerId,
        company.id // Security: pass company_id
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signer-document-${signerName.replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message.success(t("customs.documentDownloaded"));
    } catch (error) {
      console.error("Error downloading signer document:", error);
      message.error(t("customs.errors.failedToDownloadDocument"));
    }
  };

  const handleContinueSignup = async () => {
    if (!company?.leancustoms_contract_id || !company.id) return;

    setLoadingSignupUrl(true);
    try {
      const result = await LeanCustomsService.getContractSignupUrl(
        company.leancustoms_contract_id,
        company.id
      );

      if (result.success && result.data?.signup_url) {
        window.open(result.data.signup_url, '_blank');
      } else {
        message.error(result.error || t('customs.contract.getSignupUrlError') || 'Failed to get signup URL');
      }
    } catch (error) {
      console.error('Error getting signup URL:', error);
      message.error(t('customs.contract.getSignupUrlError') || 'Failed to get signup URL');
    } finally {
      setLoadingSignupUrl(false);
    }
  };

  const handleLinkOrganization = async () => {
    if (!contractWithOrganization?.organization_id || !company?.id) return;

    setLinkingOrganization(true);
    try {
      const result = await LeanCustomsService.linkOrganization(
        contractWithOrganization.organization_id,
        company.id,
        contractWithOrganization.id
      );

      if (result.success) {
        message.success(t('customs.organization.linked') || 'Organization linked successfully!');
        // Reload page data
        window.location.reload();
      } else {
        message.error(result.error || t('customs.organization.linkError') || 'Failed to link organization');
      }
    } catch (error) {
      console.error('Error linking organization:', error);
      message.error(t('customs.organization.linkError') || 'Failed to link organization');
    } finally {
      setLinkingOrganization(false);
    }
  };

  if (!company?.leancustoms_organization_id) {
    return (
      <div style={{ padding: "24px", position: "relative" }}>
        <div
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#666",
            zIndex: 1,
          }}
        >
          <span>{t("customs.poweredBy") || "Powered by"}</span>
          <img
            src="/lean_customs_logo.jpeg"
            alt="LeanCustoms"
            style={{
              height: 20,
              width: "auto",
            }}
          />
          <span style={{ fontWeight: 500 }}>LeanCustoms</span>
        </div>
        <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: "900px" }}>
          <div>
            <Space align="center" style={{ marginBottom: 8 }}>
              <Title level={2} style={{ margin: 0 }}>
                {t("customs.title")}
              </Title>
              <Tag color="orange" style={{ fontSize: 12, fontWeight: 600 }}>
                BETA
              </Tag>
            </Space>
            <Text type="secondary">
              {t("customs.setupDescription") || "Set up your LeanCustoms integration to manage customs declarations and contracts."}
            </Text>
          </div>

        {contractWithOrganization ? (
          <Card
            style={{
              marginBottom: 24,
              border: "1px solid #52c41a",
              borderRadius: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: "#52c41a" }}>
                  {t("customs.organization.readyToLink") || "Organization Ready to Link"}
                </h3>
                <p style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
                  {t("customs.organization.readyToLinkDescription") || 
                    "Your contract has been completed and an organization has been created. Click the button below to link it to your company."}
                </p>
                <Button
                  type="primary"
                  size="large"
                  loading={linkingOrganization}
                  onClick={handleLinkOrganization}
                  style={{ minWidth: 200 }}
                >
                  {t("customs.organization.linkButton") || "Link Organization"}
                </Button>
              </div>
            </div>
          </Card>
        ) : company?.leancustoms_contract_id ? (
          <Card
            style={{
              marginBottom: 24,
              border: "1px solid #faad14",
              borderRadius: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: "#faad14" }}>
                  {t("customs.contract.pendingSignup") || "Contract Created - Signup Pending"}
                </h3>
                <p style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
                  {t("customs.contract.pendingSignupDescription") || 
                    "You have created a contract but haven't completed the signup process yet. Click the button below to continue with the signup form. Or it might be pending approval, so make sure you have completed all steps in the 'Continue Signup' link."}
                </p>
                {checkingContract && (
                  <Alert
                    message={t("customs.contract.checkingSignup") || "Checking if signup is completed..."}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {!checkingContract && timeUntilNextPoll > 0 && (
                  <Alert
                    message={
                      timeUntilNextPoll === 1
                        ? t("customs.contract.nextPollSingular") || "Next check in 1 second..."
                        : t("customs.contract.nextPoll", { seconds: timeUntilNextPoll }) || `Next check in ${timeUntilNextPoll} seconds...`
                    }
                    type="info"
                    showIcon={false}
                    style={{ marginBottom: 16, fontSize: 12 }}
                  />
                )}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Button
                    type="primary"
                    size="large"
                    loading={loadingSignupUrl}
                    onClick={handleContinueSignup}
                    style={{ minWidth: 200 }}
                  >
                    {t("customs.contract.continueSignup") || "Continue Signup"}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card
            style={{
              marginBottom: 24,
              border: "1px solid #d9d9d9",
              borderRadius: 8,
            }}
          >
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
                {t("customs.getStarted.title") || "Get Started with LeanCustoms"}
              </h3>
              <p style={{ fontSize: 14, color: "#666", marginBottom: 16, lineHeight: 1.6 }}>
                {t("customs.getStarted.description") || 
                  "To use LeanCustoms for managing your customs declarations, you'll need to create a contract. This contract will be used to set up your organization, add signers, and complete the necessary verification process."}
              </p>
              <div style={{ 
                background: "#f5f5f5", 
                padding: 16, 
                borderRadius: 6, 
                marginBottom: 16 
              }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                  {t("customs.getStarted.whatHappens") || "What happens next?"}
                </h4>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: 20, 
                  fontSize: 14, 
                  color: "#666",
                  lineHeight: 1.8 
                }}>
                  <li>{t("customs.getStarted.step1") || "Create a contract with your company information"}</li>
                  <li>{t("customs.getStarted.step2") || "Complete the signup form on LeanCustoms (organization, signers, documents)"}</li>
                  <li>{t("customs.getStarted.step3") || "Your organization will be automatically linked once the signup is complete"}</li>
                  <li>{t("customs.getStarted.step4") || "Start managing your customs declarations"}</li>
                </ul>
              </div>
            </div>
            {company && (
              <CreateLeanCustomsContract
                company={company}
                onSuccess={() => {
                  // Reload page data after successful contract creation
                  if (company.id) {
                    window.location.reload();
                  }
                }}
              />
            )}
          </Card>
        )}
        </Space>
      </div>
    );
  }

  // Group declarations by status
  const groupedByStatus = declarations.reduce((acc, declaration) => {
    const status = declaration.dms_status || "unknown";
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(declaration);
    return acc;
  }, {} as Record<string, LeanCustomsDeclaration[]>);

  const sortedStatuses = Object.keys(groupedByStatus).sort((a, b) => {
    const aIndex = DECLARATION_STATUS_ORDER.indexOf(a as any);
    const bIndex = DECLARATION_STATUS_ORDER.indexOf(b as any);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div style={{ padding: "24px", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: "#666",
          zIndex: 1,
        }}
      >
        <span>{t("customs.poweredBy") || "Powered by"}</span>
        <img
          src="/lean_customs_logo.jpeg"
          alt="LeanCustoms"
          style={{
            height: 20,
            width: "auto",
          }}
        />
        <span style={{ fontWeight: 500 }}>LeanCustoms</span>
      </div>
      <Space align="center" style={{ marginBottom: 12 }}>
        <Title level={2} style={{ margin: 0 }}>
          {t("customs.title")}
        </Title>
        <Tag color="orange" style={{ fontSize: 12, fontWeight: 600 }}>
          BETA
        </Tag>
      </Space>

      <Card
        size="small"
        loading={loading}
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: "12px 16px" } }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <Space align="start" size={12}>
            <CheckCircleOutlined
              style={{ color: "#52c41a", fontSize: 22, marginTop: 2 }}
            />
            <div>
              <div style={{ fontWeight: 600 }}>
                {t("customs.connectedToLeanCustoms")}
              </div>
              {organization?.name ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {organization.name}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t("customs.connectedStripNoOrgName")}
                </Text>
              )}
            </div>
          </Space>
          <Button
            type="default"
            icon={<InfoCircleOutlined />}
            onClick={() => setIntegrationDetailsModalVisible(true)}
          >
            {t("customs.viewIntegrationDetails")}
          </Button>
        </div>
      </Card>

      <Modal
        title={t("customs.integrationDetailsModalTitle")}
        open={integrationDetailsModalVisible}
        onCancel={() => setIntegrationDetailsModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setIntegrationDetailsModalVisible(false)}
          >
            {t("common.close")}
          </Button>,
        ]}
        width={920}
        styles={{
          body: { maxHeight: "min(78vh, 720px)", overflowY: "auto" },
        }}
      >
        <LeanCustomsIntegrationDetailsContent
          organization={organization}
          contracts={contracts}
          loadingContracts={loadingContracts}
          contractSigners={contractSigners}
          loadingSigners={loadingSigners}
          contractSupportDocuments={contractSupportDocuments}
          loadingSupportDocuments={loadingSupportDocuments}
          onContractPanelsChange={handleIntegrationContractPanelsChange}
          onDownloadSignerDocument={handleDownloadSignerDocument}
          onDownloadSupportDocument={handleDownloadDocument}
        />
      </Modal>

      {/* Declarations Section */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>{t("customs.declarations")}</span>
          </Space>
        }
        extra={
          <Input.Search
            placeholder={
              t("customs.searchByReference") || "Search by reference..."
            }
            allowClear
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={(value) => {
              setSearchQuery(value);
              loadDeclarations(value);
            }}
            style={{ width: 300 }}
            enterButton
          />
        }
      >
        {loadingDeclarations ? (
          <Spin />
        ) : declarations.length > 0 ? (
          <Tabs
              defaultActiveKey={sortedStatuses[0] || "unknown"}
              items={sortedStatuses.map((status) => {
              const statusDeclarations = groupedByStatus[status];
              const statusColor =
                DECLARATION_STATUS_COLORS[status] || "default";
              const statusLabel = DECLARATION_STATUS_LABELS[status] || status;

              return {
                key: status,
                label: (
                  <Space>
                    <Tag color={statusColor}>{statusLabel}</Tag>
                    <span>({statusDeclarations.length})</span>
                  </Space>
                ),
                children: (
                  <Table
                    dataSource={statusDeclarations}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: "max-content" }}
                    onRow={(record) => ({
                      onClick: () => {
                        setSelectedDeclaration(record);
                        setDeclarationItems([]);
                        setDeclarationDocuments([]);
                        setDeclarationModalVisible(true);
                      },
                      style: { cursor: "pointer" },
                    })}
                    columns={[
                      {
                        title: t("customs.reference"),
                        dataIndex: "reference",
                        key: "reference",
                        render: (ref: string) =>
                          ref ? <BookingReferenceText text={ref} /> : "-",
                        width: 200,
                      },
                      {
                        title: t("customs.declarationStatus"),
                        dataIndex: "dms_status",
                        key: "dms_status",
                        width: 200,
                        render: (status: string | undefined) => {
                          const s = status || "unknown";
                          return (
                            <Tag
                              color={
                                DECLARATION_STATUS_COLORS[s] || "default"
                              }
                            >
                              {DECLARATION_STATUS_LABELS[s] || s}
                            </Tag>
                          );
                        },
                      },
                      {
                        title: t("customs.date"),
                        dataIndex: "date",
                        key: "date",
                        render: (date: string) =>
                          date ? new Date(date).toLocaleDateString() : "-",
                        width: 80,
                      },
                      {
                        title: t("customs.agent"),
                        dataIndex: "agent_name",
                        key: "agent_name",
                        render: (name: string) => name || "-",
                        width: 120,
                      },
                      {
                        title: t("customs.importer"),
                        dataIndex: "importer_name",
                        key: "importer_name",
                        render: (name: string) => name || "-",
                        width: 120,
                      },
                      {
                        title: t("customs.exporter"),
                        dataIndex: "exporter_name",
                        key: "exporter_name",
                        render: (name: string) => name || "-",
                        width: 150,
                      },
                      {
                        title: t("customs.incoterm"),
                        dataIndex: "incoterm",
                        key: "incoterm",
                        render: (
                          incoterm: string,
                          record: LeanCustomsDeclaration
                        ) => (record as any).incoterm || "-",
                        width: 120,
                      },
                      {
                        title: t("customs.documents"),
                        key: "documents",
                        width: 200,
                        render: (
                          _: unknown,
                          record: LeanCustomsDeclaration
                        ) => {
                          const documents = declarationDocumentsMap[record.id];
                          const isLoading = loadingDocumentsMap[record.id];

                          if (isLoading) {
                            return <Spin size="small" />;
                          }

                          if (!documents || documents.length === 0) {
                            return <span style={{ color: "#999" }}>-</span>;
                          }

                          return (
                            <Space size="small" wrap>
                              {documents.map((doc) => (
                                <Button
                                  key={doc.id}
                                  type="link"
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadDeclarationDocument(
                                      record.id,
                                      doc.id,
                                      doc.filename
                                    );
                                  }}
                                  title={doc.filename}
                                >
                                  {doc.document_type}
                                </Button>
                              ))}
                            </Space>
                          );
                        },
                      },
                      {
                        title: t("common.actions"),
                        key: "action",
                        fixed: "right" as const,
                        width: 100,
                        render: (
                          _: unknown,
                          record: LeanCustomsDeclaration
                        ) => (
                          <Button
                            type="link"
                            size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDeclaration(record);
                            setDeclarationItems([]);
                            setDeclarationDocuments([]);
                            setDeclarationModalVisible(true);
                          }}
                          >
                            {t("customs.viewDetails")}
                          </Button>
                        ),
                      },
                    ]}
                  />
                ),
              };
            })}
          />
        ) : (
          <Empty
            description={t("customs.noDeclarations")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Declaration Details Modal */}
      <Modal
        title={
          !selectedDeclaration
            ? t("customs.declarationDetails")
            : (() => {
                const ref = selectedDeclaration.reference?.trim() || "";
                const booking = String(
                  (selectedDeclaration as { booking_number?: string })
                    .booking_number || ""
                ).trim();
                const party = String(
                  (selectedDeclaration as { importer_name?: string })
                    .importer_name ||
                    (selectedDeclaration as { exporter_name?: string })
                      .exporter_name ||
                    (selectedDeclaration as { seller_name?: string })
                      .seller_name ||
                    ""
                ).trim();
                const dateStr = selectedDeclaration.date
                  ? new Date(
                      selectedDeclaration.date
                    ).toLocaleDateString()
                  : "";
                const refBooking =
                  ref && booking ? `${ref} / ${booking}` : ref || booking;
                const tail = [party, dateStr].filter(Boolean).join(" ");
                if (!refBooking && !tail) {
                  return t("customs.declarationDetails");
                }
                return [t("customs.declarationDetails"), refBooking, tail]
                  .filter(Boolean)
                  .join(" - ");
              })()
        }
        open={declarationModalVisible}
        onCancel={() => {
          setDeclarationModalVisible(false);
          setSelectedDeclaration(null);
          setDeclarationItems([]);
          setDeclarationDocuments([]);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDeclarationModalVisible(false);
              setSelectedDeclaration(null);
              setDeclarationItems([]);
              setDeclarationDocuments([]);
            }}
          >
            {t("common.close")}
          </Button>,
        ]}
        width={1200}
      >
        {selectedDeclaration && (
          <Tabs
            defaultActiveKey="basic"
            onChange={(key) => {
              if (key === "items" && selectedDeclaration.id) {
                setDeclarationItems([]);
                loadDeclarationItems(selectedDeclaration.id);
              } else if (key === "documents" && selectedDeclaration.id) {
                if (
                  declarationDocuments.length === 0 &&
                  !loadingDocuments
                ) {
                  loadDeclarationDocuments(selectedDeclaration.id);
                }
              }
            }}
            items={[
              {
                key: "basic",
                label: t("customs.basicInformation"),
                children: (
                  <>
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label={t("customs.reference")} span={2}>
                      {selectedDeclaration.reference ? (
                        <BookingReferenceText
                          text={selectedDeclaration.reference}
                        />
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="UID" span={2}>
                      <code>{selectedDeclaration.uid || "-"}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.mrn")}>
                      {selectedDeclaration.mrn || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.lrn")}>
                      {selectedDeclaration.lrn || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.type")}>
                      {selectedDeclaration.declaration_type || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.declarationTypeAdditional")}
                    >
                      {(selectedDeclaration as any)
                        .declaration_type_additional || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.status")}>
                      <Tag
                        color={
                          DECLARATION_STATUS_COLORS[
                            selectedDeclaration.dms_status || ""
                          ] || "default"
                        }
                      >
                        {DECLARATION_STATUS_LABELS[
                          selectedDeclaration.dms_status || ""
                        ] ||
                          selectedDeclaration.dms_status ||
                          "unknown"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.locked")}>
                      {(selectedDeclaration as any).locked
                        ? t("common.yes")
                        : t("common.no")}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.date")}>
                      {selectedDeclaration.date
                        ? new Date(
                            selectedDeclaration.date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.acceptanceDate")}>
                      {selectedDeclaration.acceptance_date
                        ? new Date(
                            selectedDeclaration.acceptance_date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.clearanceDate")}>
                      {selectedDeclaration.clearance_date
                        ? new Date(
                            selectedDeclaration.clearance_date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.releaseDate")}>
                      {selectedDeclaration.release_date
                        ? new Date(
                            selectedDeclaration.release_date
                          ).toLocaleDateString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.createdAt")}>
                      {selectedDeclaration.created_at
                        ? new Date(
                            selectedDeclaration.created_at
                          ).toLocaleString()
                        : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.updatedAt")}>
                      {selectedDeclaration.updated_at
                        ? new Date(
                            selectedDeclaration.updated_at
                          ).toLocaleString()
                        : "-"}
                    </Descriptions.Item>
                  </Descriptions>
                  <Divider orientation="left">
                    {t("customs.downloadableDocuments")}
                  </Divider>
                  {loadingDocuments ? (
                    <Spin />
                  ) : (
                    <>
                      {declarationDocuments.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <Text
                            strong
                            style={{ display: "block", marginBottom: 8 }}
                          >
                            {t("customs.documents")}
                          </Text>
                          <Space wrap>
                            {declarationDocuments.map((doc) => (
                              <Button
                                key={doc.id}
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() =>
                                  selectedDeclaration.id &&
                                  handleDownloadDeclarationDocument(
                                    selectedDeclaration.id,
                                    doc.id,
                                    doc.filename
                                  )
                                }
                                title={doc.filename}
                              >
                                {doc.document_type || doc.filename}
                              </Button>
                            ))}
                          </Space>
                        </div>
                      )}
                      {declarationDocuments.length === 0 && (
                          <Empty
                            description={t("customs.noDocuments")}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        )}
                    </>
                  )}
                  </>
                ),
              },
              {
                key: "parties",
                label: t("customs.parties"),
                children: (
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label={t("customs.agent")} span={2}>
                      <strong>{selectedDeclaration.agent_name || "-"}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.agentEori")}>
                      {(selectedDeclaration as any).agent_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.agentFunction")}>
                      {(selectedDeclaration as any)
                        .agent_function_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.declarant")} span={2}>
                      <strong>
                        {selectedDeclaration.declarant_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.declarantEori")}>
                      {(selectedDeclaration as any).declarant_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.declarantVatNumber")}>
                      {(selectedDeclaration as any).declarant_vat_number || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.importer")} span={2}>
                      <strong>
                        {selectedDeclaration.importer_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.importerEori")}>
                      {(selectedDeclaration as any).importer_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.exporter")} span={2}>
                      <strong>
                        {selectedDeclaration.exporter_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.exporterEori")}>
                      {(selectedDeclaration as any).exporter_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.buyer")} span={2}>
                      <strong>{selectedDeclaration.buyer_name || "-"}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.buyerEori")}>
                      {(selectedDeclaration as any).buyer_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.seller")} span={2}>
                      <strong>{selectedDeclaration.seller_name || "-"}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.sellerEori")}>
                      {(selectedDeclaration as any).seller_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.consignee")} span={2}>
                      <strong>
                        {(selectedDeclaration as any).consignee_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.consigneeEori")}>
                      {(selectedDeclaration as any).consignee_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.forwarder")} span={2}>
                      <strong>
                        {selectedDeclaration.forwarder_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.fiscalRepresentative")}
                      span={2}
                    >
                      <strong>
                        {(selectedDeclaration as any)
                          .fiscal_representative_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.payer")} span={2}>
                      <strong>
                        {(selectedDeclaration as any).payer_name || "-"}
                      </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.payerEori")}>
                      {(selectedDeclaration as any).payer_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.guaranteeEori")}>
                      {(selectedDeclaration as any).guarantee_eori || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.transporter")}
                      span={2}
                    >
                      <strong>
                        {(selectedDeclaration as any).transporter_name || "-"}
                      </strong>
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: "transport",
                label: t("customs.transport"),
                children: (
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item
                      label={t("customs.borderTransportMode")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .border_transport_mode_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.borderTransportIdentification")}
                    >
                      {(selectedDeclaration as any)
                        .border_transport_identification || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.borderTransportIdentificationType")}
                    >
                      {(selectedDeclaration as any)
                        .border_transport_identification_type_description ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.borderTransportNationality")}
                    >
                      {(selectedDeclaration as any)
                        .border_transport_nationality_code || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.departureTransportMode")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .departure_transport_mode_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.departureTransportIdentification")}
                    >
                      {(selectedDeclaration as any)
                        .departure_transport_identification || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.departureTransportIdentificationType")}
                    >
                      {(selectedDeclaration as any)
                        .departure_transport_identification_type_description ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.departureTransportNationality")}
                    >
                      {(selectedDeclaration as any)
                        .departure_transport_nationality_code || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.arrivalTransportMode")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .arrival_transport_mode_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.arrivalTransportIdentification")}
                    >
                      {(selectedDeclaration as any)
                        .arrival_transport_identification || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.arrivalTransportIdentificationType")}
                    >
                      {(selectedDeclaration as any)
                        .arrival_transport_identification_type_description ||
                        "-"}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: "location",
                label: t("customs.location"),
                children: (
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item
                      label={t("customs.declarationOffice")}
                      span={2}
                    >
                      {(selectedDeclaration as any).declaration_office_name ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.declarationOfficeId")}>
                      {(selectedDeclaration as any).declaration_office_id ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.exitOffice")} span={2}>
                      {(selectedDeclaration as any).exit_office_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.exitOfficeId")}>
                      {(selectedDeclaration as any).exit_office_id || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.goodsLocationType")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .goods_location_type_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.goodsLocationIdentificationType")}
                    >
                      {(selectedDeclaration as any)
                        .goods_location_identification_type_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.goodsLocationCountry")}
                    >
                      {(selectedDeclaration as any)
                        .goods_location_country_code || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.warehouse")} span={2}>
                      {(selectedDeclaration as any).warehouse_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.terminal")} span={2}>
                      {(selectedDeclaration as any).terminal_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.dispatchCountry")}>
                      {(selectedDeclaration as any).dispatch_country_code ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.destinationCountry")}>
                      {(selectedDeclaration as any).destination_country_code ||
                        "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.exportationCountry")}>
                      {(selectedDeclaration as any).exportation_country_code ||
                        "-"}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: "procedures",
                label: t("customs.procedures"),
                children: (
                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item
                      label={t("customs.procedureCurrent")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .procedure_current_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.procedurePrevious")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .procedure_previous_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.procedureSpecial")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .procedure_special_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.transactionNature")}
                      span={2}
                    >
                      {(selectedDeclaration as any)
                        .transaction_nature_description || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("customs.incoterm")} span={2}>
                      {(selectedDeclaration as any).incoterm || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("customs.packageType")}
                      span={2}
                    >
                      {(selectedDeclaration as any).package_type_description ||
                        "-"}
                    </Descriptions.Item>
                  </Descriptions>
                ),
              },
              {
                key: "documents",
                label: t("customs.documents"),
                children: (
                  <div>
                    {loadingDocuments ? (
                      <Spin />
                    ) : declarationDocuments.length > 0 ? (
                      <Table
                        dataSource={declarationDocuments}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        columns={[
                          {
                            title: t("customs.documentType"),
                            dataIndex: "document_type",
                            key: "document_type",
                            width: 150,
                            render: (type: string) => (
                              <Tag color="blue">{type || "-"}</Tag>
                            ),
                          },
                          {
                            title: t("customs.filename"),
                            dataIndex: "filename",
                            key: "filename",
                            width: 250,
                          },
                          {
                            title: t("customs.fileType"),
                            dataIndex: "content_type",
                            key: "content_type",
                            width: 120,
                          },
                          {
                            title: t("customs.fileSize"),
                            dataIndex: "byte_size",
                            key: "byte_size",
                            width: 100,
                            render: (size: number) => {
                              if (size < 1024) return `${size} B`;
                              if (size < 1024 * 1024)
                                return `${(size / 1024).toFixed(2)} KB`;
                              return `${(size / (1024 * 1024)).toFixed(2)} MB`;
                            },
                          },
                          {
                            title: t("customs.provisional"),
                            dataIndex: "provisional",
                            key: "provisional",
                            width: 100,
                            render: (provisional: boolean) => (
                              <Tag color={provisional ? "orange" : "green"}>
                                {provisional
                                  ? t("customs.provisional") || "Provisional"
                                  : t("customs.definitive") || "Definitive"}
                              </Tag>
                            ),
                          },
                          {
                            title: t("customs.dateUploaded"),
                            dataIndex: "created_at",
                            key: "created_at",
                            width: 150,
                            render: (date: string) => {
                              if (!date) return "-";
                              return new Date(date).toLocaleString();
                            },
                          },
                          {
                            title: t("common.actions"),
                            key: "action",
                            fixed: "right" as const,
                            width: 120,
                            render: (
                              _: unknown,
                              doc: LeanCustomsDeclarationDocument
                            ) => (
                              <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() =>
                                  selectedDeclaration?.id &&
                                  handleDownloadDeclarationDocument(
                                    selectedDeclaration.id,
                                    doc.id,
                                    doc.filename
                                  )
                                }
                              >
                                {t("customs.download")}
                              </Button>
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <Empty
                        description={t("customs.noDocuments")}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: "items",
                label: t("customs.items"),
                children: (
                  <div>
                    {loadingItems ? (
                      <Spin />
                    ) : declarationItems.length > 0 ? (
                      <Table
                        dataSource={declarationItems}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ x: "max-content" }}
                        columns={[
                          {
                            title: t("customs.sequenceNumber"),
                            dataIndex: "sequence_number",
                            key: "sequence_number",
                            width: 80,
                          },
                          {
                            title: t("customs.description"),
                            dataIndex: "description",
                            key: "description",
                            render: (desc: string) =>
                              desc ? <BookingReferenceText text={desc} /> : "-",
                            width: 200,
                          },
                          {
                            title: t("customs.goodsNomenclatureCode"),
                            dataIndex: "goods_nomenclature_code",
                            key: "goods_nomenclature_code",
                            render: (code: string) => code || "-",
                            width: 150,
                          },
                          {
                            title: t("customs.customsValue"),
                            dataIndex: "customs_value",
                            key: "customs_value",
                            width: 120,
                          },
                          {
                            title: t("customs.grossWeight"),
                            dataIndex: "gross_weight",
                            key: "gross_weight",
                            width: 100,
                          },
                          {
                            title: t("customs.netWeight"),
                            dataIndex: "net_weight",
                            key: "net_weight",
                            width: 100,
                          },
                          {
                            title: t("customs.packageCount"),
                            dataIndex: "package_count",
                            key: "package_count",
                            width: 100,
                          },
                          {
                            title: t("customs.originCountry"),
                            dataIndex: "origin_country_code",
                            key: "origin_country_code",
                            render: (code: string) => code || "-",
                            width: 100,
                          },
                          {
                            title: t("customs.invoiceAmount"),
                            dataIndex: "invoice_amount",
                            key: "invoice_amount",
                            render: (
                              amount: number,
                              record: LeanCustomsDeclarationItem
                            ) =>
                              amount
                                ? `${amount} ${record.invoice_currency || ""}`
                                : "-",
                            width: 120,
                          },
                          {
                            title: t("customs.procedureCurrent"),
                            dataIndex: "procedure_current_description",
                            key: "procedure_current_description",
                            render: (desc: string) => desc || "-",
                            width: 150,
                          },
                        ]}
                      />
                    ) : (
                      <Empty
                        description={t("customs.noItems")}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};
